import { useCallback } from "react";
import { Flex, FlexProps, Status, Text, useToast } from "@chakra-ui/react";

import { CloseIcon } from "../../assets/icons";
import { StatusIcon } from "./StatusIcon";
import { GeneralTestIds } from "../../../dataTestIds";
import { textStyles } from "../../../theme/foundations";
import { getGapStyles } from "../../../theme/utils";

interface ToastProps {
  title: string;
  description?: string;
  status?: Omit<Status, "default">;
  onClose?: () => void;
}

const Toast = ({ status = "info", title, description, onClose }: ToastProps) => (
  <Flex {...ToastStyles(status)} data-testid={GeneralTestIds.toast.file}>
    <StatusIcon status={status} />

    <Flex direction="column" {...getGapStyles(1, true)} color="black">
      <Text {...textStyles.alertLg}>{title}</Text>
      {description && <Text {...textStyles.alertMd}>{description}</Text>}
    </Flex>

    <CloseIcon onClick={onClose} style={{ cursor: "pointer", margin: -4, marginLeft: "auto", color: "black" }} />
  </Flex>
);

export const usePastimeToast = () => {
  const toast = useToast({ position: "top" });

  const showToast = useCallback(
    ({ status = "info", title, description }: Omit<ToastProps, "onClose">) => {
      toast({
        render: ({ onClose }) => <Toast title={title} description={description} status={status} onClose={onClose} />,
      });
    },
    [toast]
  );

  return showToast;
};

const ToastStyles = (status: Omit<Status, "default"> = "info"): FlexProps => ({
  ...getGapStyles(4),
  color: status as string,
  bg: getToastBackground(status),
  borderRadius: "md",
  p: 4,
});

const getToastBackground = (status: Omit<Status, "default"> = "info") => {
  switch (status) {
    case "success":
      return "green.50";
    case "warning":
      return "yellow.50";
    case "error":
      return "red.50";
    default:
      return "blue.50";
  }
};
